import { Box, Typography } from '@mui/material'
import List from 'components/core/list'
import { C1TableRedux, C1TableReduxColumn } from 'components/core/list/C1Table/C1TableRedux'
import { AppDisplayName } from 'components/pages/app/app-display-name'
import { C1EntitlementDisplayName } from 'components/pages/entitlement/display-name'
import { EntitlementsDrawer } from 'components/pages/entitlement/drawer'
import { appEntitlementId } from 'components/pages/entitlement/helpers'
import {
    AppEntitlementFilters,
    AppEntitlementSearchFilter,
} from 'components/pages/requestcatalog/details/setup/add_entitlements/list/AppEntitlementSearchFilter'
import { EntitlementListStyles } from 'components/pages/requestcatalog/details/setup/add_entitlements/list/styles'
import { AppEntitlementValue } from 'lib/actions/apps/app-entitlement'
import { App } from 'pbts/c1/api/app/v1/app'
import { AppEntitlementSearchServiceSearchRequest } from 'pbts/c1/api/app/v1/app_entitlement'
import { useCallback, useMemo, useState } from 'react'
import { FormattedMessage, FormattedNumber } from 'react-intl'
import { appEntitlementsSearchApi } from 'redux_toolkit/entitlements'

type BaseEntitlementListProps = {
    disableEntitlementDrawer?: boolean
    hiddenFilters?: Array<AppEntitlementFilters>
    excludeResourceTypeIds?: string[]
}

type MultipleSelectionProps = BaseEntitlementListProps & {
    selectionMode?: 'multiple'
    setSelectedEntitlements: (selected: AppEntitlementValue[]) => void
    selectedEntitlements: AppEntitlementValue[]
    disabledEntitlements?: AppEntitlementValue[]
    onRowClick?: never
}

type SingleSelectionProps = BaseEntitlementListProps & {
    selectionMode: 'single'
    onRowClick: (event: React.MouseEvent | React.KeyboardEvent, row: AppEntitlementValue) => void
    setSelectedEntitlements?: never
    selectedEntitlements?: never
    disabledEntitlements?: never
}

type EntitlementListProps = MultipleSelectionProps | SingleSelectionProps

interface injectEntitlementListExtraProps {
    disableEntitlementDrawer: boolean
    selectionMode: 'multiple' | 'single'
}

const cols: Array<C1TableReduxColumn<AppEntitlementValue, injectEntitlementListExtraProps>> = [
    {
        field: 'entitlement',
        label: (
            <FormattedMessage
                id="components_catalog_entitlement_picker_table_entitlement"
                defaultMessage="Entitlement"
            />
        ),
        flex: 4,
        maxWidth: '32em',
        Component: ({
            row: entitlement,
            extras,
        }: { row: AppEntitlementValue; extras: injectEntitlementListExtraProps }): React.ReactElement => {
            const entitlementDrawerContent = <EntitlementsDrawer entitlementId={entitlement.id} appId={entitlement.appId} />
            return <Box sx={{ flex: 1 }}>
                <C1EntitlementDisplayName
                    readOnly={extras.selectionMode === 'single'}
                    entitlement={entitlement}
                    drawerContent={extras.disableEntitlementDrawer || extras.selectionMode === 'single' ? undefined : entitlementDrawerContent}
                />
            </Box>
        },
    },
    {
        field: 'application',
        label: <FormattedMessage id="components_catalog_entitlement_picker_table_app" defaultMessage="Application" />,
        flex: 3,
        Component: ({ row }) => <AppDisplayName app={row.app || App.create()} />,
    },
    {
        field: 'description',
        flex: 4,
        label: (
            <FormattedMessage
                id="components_catalog_entitlement_picker_table_grant_desc"
                defaultMessage="Description"
            />
        ),
        Component: ({ row }) => <Typography>{row.description}</Typography>,
    },
    {
        field: 'grantCount',
        label: (
            <FormattedMessage
                id="components_catalog_entitlement_picker_table_grant_count"
                defaultMessage="Grant count"
            />
        ),
        Component: ({ row }) => <Typography><FormattedNumber value={row.grantCount} /></Typography>,
    },
]

export const EntitlementList = (props: EntitlementListProps): React.ReactElement => {
    const {
        hiddenFilters = ['includeDeleted'],
        disableEntitlementDrawer = false,
        excludeResourceTypeIds = [],
    } = props

    const selectionMode = props.selectionMode ?? 'multiple'

    // Type-safe access to selection-related props
    const noOpSetSelections = useCallback((): void => {
        // No-op function for single selection mode
    }, [])

    const setSelectedEntitlements = selectionMode === 'multiple' ? props.setSelectedEntitlements : noOpSetSelections
    const selectedEntitlements = selectionMode === 'multiple' ? props.selectedEntitlements : []
    const disabledEntitlements = selectionMode === 'multiple' ? props.disabledEntitlements : undefined
    const onRowClick = selectionMode === 'single' ? props.onRowClick : undefined
    const [filter, setFilter] = useState<AppEntitlementSearchServiceSearchRequest>(
        AppEntitlementSearchServiceSearchRequest.create({
            expandMask: { paths: ['*'] },
            isAutomated: false,
            excludeResourceTypeIds,
        }),
    )

    const {
        data: allEntitlements,
        isLoading,
        isError,
        getPage,
        canLoadMore,
        error: listEntitlementsError,
    } = appEntitlementsSearchApi.useSearch(filter)

    const [page, setPage] = useState<number>(0)

    const onPageChange = useCallback(
        async (n: number) => {
            await getPage(n)
            setPage(n)
        },
        [getPage],
    )

    const onFilterChange = useCallback((searchFilter: AppEntitlementSearchServiceSearchRequest): void => {
        setFilter(searchFilter)
        setPage(0)
    }, [setFilter])

    const search = useMemo(() => <AppEntitlementSearchFilter
        onChange={onFilterChange}
        initialState={filter}
        hiddenFilters={hiddenFilters}
    />, [filter, hiddenFilters, onFilterChange])

    const table = (
        <C1TableRedux<AppEntitlementValue, injectEntitlementListExtraProps>
            data={allEntitlements}
            cols={cols}
            canLoadMore={canLoadMore}
            isError={isError}
            isLoading={isLoading}
            onPageChange={onPageChange}
            error={listEntitlementsError}
            page={page}
            selections={selectionMode === 'multiple' ? selectedEntitlements : undefined}
            staticExtraProps={{
                disableEntitlementDrawer,
                selectionMode,
            }}
            disabledSelections={selectionMode === 'multiple' ? disabledEntitlements : undefined}
            setSelections={selectionMode === 'multiple' ? setSelectedEntitlements : undefined}
            onRowClick={selectionMode === 'single' ? onRowClick : undefined}
            getId={appEntitlementId}
        />
    )

    return (
        <Box sx={EntitlementListStyles.container}>
            <List<AppEntitlementValue> search={search} table={table} />
        </Box>
    )
}
